import { useAtomValue, useSetAtom } from 'jotai';
import { loadingCounterAtom, userDataAtom } from '../Atoms';
import fetch from '../api/fetch';

// Set before reloading on an expired session; the login page reads it to say why.
export const SESSION_EXPIRED_FLAG = 'sessionExpired';

export default function useProxy() {
  const setLoadingCounter = useSetAtom(loadingCounterAtom);
  const userData = useAtomValue(userDataAtom);

  function FetchError(status = 500, payload = 'Application error') {
    this.name = 'FetchError';
    this.payload = payload;
    this.status = status;
  }
  FetchError.prototype = Error.prototype;
  return {
    run: (url, method, payload, onSuccess, onFailure, params = {}) => {
      const {
        silent = false,
        doNotStringify,
        preventRedirectToLogin = false,
        signal,
        ...pagingArgs
      } = params;
      let authError = false;
      if (!silent) {
        setLoadingCounter((c) => c + 1);
      }
      const payloadToSend = !doNotStringify ? JSON.stringify(payload) : payload;

      return fetch(url, {
        method,
        cache: 'no-cache',
        body: method !== 'get' && payloadToSend ? payloadToSend : null,
        redirect: 'follow',
        mode: 'cors',
        referrerPolicy: 'no-referrer-when-downgrade',
        signal,
        ...pagingArgs,
        multipart: doNotStringify,
      })
        .then((response) => {
          const { ok, status } = response;
          if (ok) {
            const headers = {};
            for (const [key, value] of response.headers.entries()) {
              headers[key] = value;
            }

            const contentTypes = response.headers?.get('content-type') || '';

            if (
              contentTypes?.includes(
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              )
            ) {
              return response?.blob().then((data) => {
                return {
                  ok,
                  status,
                  headers,
                  data,
                };
              });
            }
            if (
              !contentTypes?.includes('application/json') &&
              !doNotStringify
            ) {
              return {
                ok,
                status,
                headers,
                data: response,
              };
            }
            if (response.status === 204) {
              return {
                ok,
                status,
                headers,
                data: {},
              };
            }

            return response?.json().then((data) => {
              return {
                ok,
                status,
                headers,
                data,
              };
            });
          }
          if (response.status === 401 && !preventRedirectToLogin) {
            authError = true;
          }
          return response.text().then((text) => {
            let result = text;
            try {
              if (text) result = JSON.parse(text);
            } catch {
              result = text;
            }
            return Promise.reject(new FetchError(status, result));
          });
        })
        .then(
          (response) => {
            if (!silent) {
              setLoadingCounter((c) => c - 1);
            }
            if (onSuccess) onSuccess(response.data);

            return response;
          },
          (error) => {
            if (!silent) {
              setLoadingCounter((c) => c - 1);
            }
            if (onFailure) onFailure(error);

            if (authError && userData) {
              sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
              window.location.assign('/');
            }
            return error;
          }
        )
        .catch((err) => {
          if (onFailure) {
            onFailure(err);
          }
          return err;
        });
    },
  };
}
